import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import {
  TrendingUpIcon,
  DollarIcon,
  ClockIcon,
  CheckIcon,
  DownloadIcon,
  AnalyticsIcon,
  AlertIcon,
  OrdersIcon,
} from '../UI/Icons';
import { Vendor } from '../../utils/constants';
import orderService, { Order } from '../../services/orderService';
import vendorService from '../../services/vendorService';
import StatTile from '../Dashboard/StatTile';
import Modal from '../UI/Modal';
import styles from './Analytics.module.css';

interface AnalyticsProps {
  vendors?: Vendor[];
  data?: unknown;
}

interface ReportData {
  generatedAt: string;
  timeRange: string;
  customDateRange: { start: string; end: string } | null;
  selectedVendors: string[];
  overview: Record<string, number>;
  vendorPerformance: Array<Record<string, string | number>>;
  popularItems: Array<{ name: string; orders: number; revenue: number }>;
  orderStatusData: Array<{ status: string; count: number; percentage: number }>;
  timeseries: Array<{ date: string; orders: number; revenue: number }>;
}

const TIME_RANGE_OPTIONS = [
  { value: '1d', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom Range' },
];

const TOOLTIP_STYLE: React.CSSProperties = {
  background: 'var(--color-surface-primary)',
  border: '1px solid var(--color-border-primary)',
  borderRadius: 8,
};

function getStatsVendorId(scopedVendorId: string | null, selectedVendorIds: string[]): string | undefined {
  if (scopedVendorId) return scopedVendorId;
  if (selectedVendorIds.length === 1) return selectedVendorIds[0];
  return undefined;
}

const Analytics: React.FC<AnalyticsProps> = ({ vendors: initialVendors = [] }) => {
  const { token } = useAuth();
  const [timeRange, setTimeRange] = useState('7d');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>(initialVendors);
  const [loading, setLoading] = useState(true);
  const [selectedVendorIds, setSelectedVendorIds] = useState<string[]>([]);
  const [serverStats, setServerStats] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scopedVendorId, setScopedVendorId] = useState<string | null>(null);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const vendorDropdownRef = useRef<HTMLDivElement>(null);
  const STADIUM_TZ = (process.env.REACT_APP_STADIUM_TZ || '').trim();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [ordersData, vendorsData] = await Promise.all([
          scopedVendorId ? orderService.getAll({ vendorId: scopedVendorId }) : orderService.getAll(),
          vendorService.getAll(),
        ]);
        setOrders(ordersData);
        setVendors(vendorsData);
      } catch (err) {
        console.error('Error loading analytics data:', err);
        setError('Failed to load analytics data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [scopedVendorId]);

  useEffect(() => {
    if (!token) return;
    try {
      const payload = authService.decodeToken(token) as Record<string, unknown>;
      const role = (payload?.role || (Array.isArray(payload?.roles) ? payload.roles[0] : null) || '').toString();
      const claimedVendor = payload?.vendorId || payload?.vendor_id || (payload?.vendor as { id?: string })?.id || null;
      const admin = role === 'admin' || role === 'super-admin';
      if (!admin && claimedVendor) {
        const id = String(claimedVendor);
        setScopedVendorId(id);
        setSelectedVendorIds((prev) => (prev.length === 1 && prev[0] === id ? prev : [id]));
      } else {
        setScopedVendorId(null);
      }
    } catch {
      // ignore decode errors
    }
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tr = params.get('range');
    const start = params.get('start');
    const end = params.get('end');
    const vendorsParam = params.get('vendors');
    if (tr && ['1d', '7d', '30d', '90d', 'custom'].includes(tr)) {
      setTimeRange(tr);
    }
    if (tr === 'custom' && start && end) {
      setCustomDateRange({ start, end });
    }
    if (vendorsParam) {
      const ids = vendorsParam.split(',').filter(Boolean);
      if (ids.length) setSelectedVendorIds(ids);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('range', timeRange);
    if (timeRange === 'custom' && customDateRange.start && customDateRange.end) {
      params.set('start', customDateRange.start);
      params.set('end', customDateRange.end);
    } else {
      params.delete('start');
      params.delete('end');
    }
    if (scopedVendorId) {
      params.set('vendors', scopedVendorId);
    } else if (selectedVendorIds.length) {
      params.set('vendors', selectedVendorIds.join(','));
    } else {
      params.delete('vendors');
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, [timeRange, customDateRange, selectedVendorIds, scopedVendorId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(e.target as Node)) {
        setVendorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const dayMs = 24 * 60 * 60 * 1000;
        const end = new Date();
        let start: Date | null = null;

        switch (timeRange) {
          case '1d':
            start = new Date(Date.now() - dayMs);
            break;
          case '7d':
            start = new Date(Date.now() - 7 * dayMs);
            break;
          case '30d':
            start = new Date(Date.now() - 30 * dayMs);
            break;
          case '90d':
            start = new Date(Date.now() - 90 * dayMs);
            break;
          case 'custom':
            if (customDateRange.start && customDateRange.end) {
              start = new Date(customDateRange.start);
              start.setHours(0, 0, 0, 0);
              const e = new Date(customDateRange.end);
              e.setHours(23, 59, 59, 999);
              const statsVendorId = getStatsVendorId(scopedVendorId, selectedVendorIds);
              const stats = await orderService.getStats({
                startDate: start.toISOString(),
                endDate: e.toISOString(),
                ...(statsVendorId ? { vendorId: statsVendorId } : {}),
              });
              setServerStats(stats);
              return;
            }
            setServerStats(null);
            return;
          default:
            setServerStats(null);
            return;
        }

        if (start) {
          const statsVendorId = getStatsVendorId(scopedVendorId, selectedVendorIds);
          const stats = await orderService.getStats({
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            ...(statsVendorId ? { vendorId: statsVendorId } : {}),
          });
          setServerStats(stats);
        } else {
          setServerStats(null);
        }
      } catch {
        setServerStats(null);
      }
    };
    fetchStats();
  }, [timeRange, customDateRange, selectedVendorIds, scopedVendorId]);

  const analyticsData = useMemo(() => {
    const toStartOfDay = (d: Date) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x.getTime();
    };
    const toEndOfDay = (d: Date) => {
      const x = new Date(d);
      x.setHours(23, 59, 59, 999);
      return x.getTime();
    };
    const formatTZDate = (d: Date): string => {
      const dtf = new Intl.DateTimeFormat('en-CA', {
        timeZone: STADIUM_TZ || undefined,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      return dtf.format(d);
    };
    const isTZActive = Boolean(STADIUM_TZ) && timeRange === 'custom' && customDateRange.start && customDateRange.end;

    const getRangeBounds = () => {
      const now = Date.now();
      const day = 24 * 60 * 60 * 1000;
      switch (timeRange) {
        case '1d':
          return { start: now - day, end: now };
        case '7d':
          return { start: now - 7 * day, end: now };
        case '30d':
          return { start: now - 30 * day, end: now };
        case '90d':
          return { start: now - 90 * day, end: now };
        case 'custom':
          if (!customDateRange.start || !customDateRange.end) return {};
          if (!isTZActive) {
            return {
              start: toStartOfDay(new Date(customDateRange.start)),
              end: toEndOfDay(new Date(customDateRange.end)),
            };
          }
          return {
            tzStartStr: formatTZDate(new Date(customDateRange.start)),
            tzEndStr: formatTZDate(new Date(customDateRange.end)),
          };
        default:
          return {};
      }
    };

    const bounds = getRangeBounds() as {
      start?: number;
      end?: number;
      tzStartStr?: string;
      tzEndStr?: string;
    };
    const { start, end, tzStartStr, tzEndStr } = bounds;
    const minutesFromMs = (ms?: number) => (typeof ms === 'number' ? ms / 1000 / 60 : undefined);

    const effectiveVendorIds = scopedVendorId ? [scopedVendorId] : selectedVendorIds;
    const vendorFiltered = effectiveVendorIds.length
      ? orders.filter((o) => effectiveVendorIds.includes(String(o.vendorId)))
      : orders;

    const filteredOrders = vendorFiltered.filter((order) => {
      if (tzStartStr && tzEndStr && isTZActive) {
        const d = order.createdAt ? new Date(order.createdAt) : (order.placedAt ? new Date(order.placedAt) : null);
        if (!d) return false;
        const dayStr = formatTZDate(d);
        return dayStr >= tzStartStr && dayStr <= tzEndStr;
      }
      const createdMs = order.createdAt ? new Date(order.createdAt).getTime() : undefined;
      const placedMs = order.placedAt ? new Date(order.placedAt).getTime() : undefined;
      const ts = createdMs ?? placedMs;
      if (!start && !end) return true;
      if (!ts) return true;
      if (start && ts < start) return false;
      if (end && ts > end) return false;
      return true;
    });

    const deliveredOrders = filteredOrders.filter((o) => o.status === 'delivered');
    const statsTotals = serverStats?.totals as Record<string, number> | undefined;

    const totalRevenue = statsTotals?.revenue ?? deliveredOrders.reduce((sum, o) => sum + o.totalAmount, 0);
    const avgOrderValue = statsTotals?.avgOrderValue ?? (deliveredOrders.length ? totalRevenue / deliveredOrders.length : 0);

    const vendorStats: Record<string, {
      vendorId: string;
      name: string;
      orders: number;
      delivered: number;
      revenue: number;
      basketTotal: number;
      prepDurations: number[];
      endToEndDurations: number[];
    }> = {};

    const overallPrepDurations: number[] = [];
    const overallEndToEnd: number[] = [];

    filteredOrders.forEach((order) => {
      if (!vendorStats[order.vendorId]) {
        vendorStats[order.vendorId] = {
          vendorId: order.vendorId,
          name: order.vendorName,
          orders: 0,
          delivered: 0,
          revenue: 0,
          basketTotal: 0,
          prepDurations: [],
          endToEndDurations: [],
        };
      }
      const vendor = vendorStats[order.vendorId];
      vendor.orders += 1;
      vendor.revenue += order.totalAmount;
      vendor.basketTotal += order.totalAmount;

      const prepMs = order.preparingToReadyMs ?? order.pendingToPreparingMs;
      const deliveredTs = order.deliveredAt || order.completedAt;
      const endToEndMs = order.createdAt && deliveredTs
        ? new Date(deliveredTs).getTime() - new Date(order.createdAt).getTime()
        : undefined;

      if (typeof prepMs === 'number') {
        vendor.prepDurations.push(prepMs);
        const prepMinutes = minutesFromMs(prepMs);
        if (typeof prepMinutes === 'number') overallPrepDurations.push(prepMinutes);
      }
      if (typeof endToEndMs === 'number' && endToEndMs > 0 && endToEndMs <= 24 * 60 * 60 * 1000) {
        vendor.endToEndDurations.push(endToEndMs);
        const totalMinutes = minutesFromMs(endToEndMs);
        if (typeof totalMinutes === 'number') overallEndToEnd.push(totalMinutes);
      }
      if (order.status === 'delivered') vendor.delivered += 1;
    });

    const average = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0);

    let vendorPerformance = Object.values(vendorStats)
      .map((v) => ({
        vendorId: v.vendorId,
        name: v.name,
        orders: v.orders,
        delivered: v.delivered,
        revenue: v.revenue,
        averageBasket: v.orders ? v.basketTotal / v.orders : 0,
        avgPrepMinutes: average(v.prepDurations.map((ms) => minutesFromMs(ms) || 0)),
        avgEndToEndMinutes: average(v.endToEndDurations.map((ms) => minutesFromMs(ms) || 0)),
        completionRate: v.orders ? (v.delivered / v.orders) * 100 : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);

    const serverVendors = serverStats?.vendors;
    if (Array.isArray(serverVendors) && serverVendors.length) {
      vendorPerformance = serverVendors.map((v: Record<string, unknown>) => ({
        vendorId: String(v.vendorId ?? v.id ?? ''),
        name: (v.name as string) ?? 'Vendor',
        orders: (v.orders as number) ?? 0,
        delivered: (v.delivered as number) ?? 0,
        revenue: (v.revenue as number) ?? 0,
        averageBasket: (v.avgOrderValue as number) ?? ((v.orders as number) ? ((v.revenue as number) ?? 0) / (v.orders as number) : 0),
        avgPrepMinutes: (v.avgPrepMinutes as number) ?? 0,
        avgEndToEndMinutes: (v.avgEndToEndMinutes as number) ?? 0,
        completionRate: (v.orders as number) ? (((v.delivered as number) ?? 0) / (v.orders as number)) * 100 : 0,
      })).sort((a, b) => b.revenue - a.revenue);
    }

    const itemStats: Record<string, { name: string; orders: number; revenue: number }> = {};
    filteredOrders.forEach((order) => {
      (order.items || []).forEach((item) => {
        if (!itemStats[item.name]) itemStats[item.name] = { name: item.name, orders: 0, revenue: 0 };
        itemStats[item.name].orders += item.quantity;
        itemStats[item.name].revenue += item.price * item.quantity;
      });
    });

    let popularItems = Object.values(itemStats).sort((a, b) => b.orders - a.orders).slice(0, 8);
    const serverItems = serverStats?.items;
    if (Array.isArray(serverItems) && serverItems.length) {
      popularItems = serverItems
        .map((it: Record<string, unknown>) => ({
          name: it.name as string,
          orders: (it.orders as number) ?? 0,
          revenue: (it.revenue as number) ?? 0,
        }))
        .sort((a, b) => b.orders - a.orders)
        .slice(0, 8);
    }

    const statusTypes: Order['status'][] = [
      'delivered', 'out-for-delivery', 'ready', 'preparing', 'confirmed', 'pending', 'cancelled',
    ];

    let orderStatusData = statusTypes.map((status) => {
      const count = filteredOrders.filter((o) => o.status === status).length;
      return { status, count, percentage: filteredOrders.length ? (count / filteredOrders.length) * 100 : 0 };
    });

    const serverStatus = serverStats?.status as Record<string, number> | undefined;
    if (serverStatus && typeof serverStatus === 'object') {
      const total = Object.values(serverStatus).reduce((a, b) => a + b, 0);
      orderStatusData = statusTypes.map((status) => {
        const count = Number(serverStatus[status] ?? 0);
        return { status, count, percentage: total ? (count / total) * 100 : 0 };
      });
    }

    let timeseries: { date: string; orders: number; revenue: number }[] = [];
    const serverTimeseries = serverStats?.timeseries;
    if (Array.isArray(serverTimeseries)) {
      timeseries = serverTimeseries.map((p: Record<string, unknown>) => ({
        date: p.date as string,
        orders: Number(p.orders ?? 0),
        revenue: Number(p.revenue ?? 0),
      }));
    } else {
      const byDay: Record<string, { orders: number; revenue: number }> = {};
      filteredOrders.forEach((o) => {
        const d = o.createdAt ? new Date(o.createdAt) : new Date();
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (!byDay[key]) byDay[key] = { orders: 0, revenue: 0 };
        byDay[key].orders += 1;
        if (o.status === 'delivered') byDay[key].revenue += o.totalAmount;
      });
      timeseries = Object.entries(byDay)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, v]) => ({ date, orders: v.orders, revenue: v.revenue }));
    }

    return {
      overview: {
        totalOrders: filteredOrders.length,
        deliveredOrders: deliveredOrders.length,
        cancelledOrders: filteredOrders.filter((o) => o.status === 'cancelled').length,
        totalRevenue,
        averageOrderValue: avgOrderValue,
        averagePrepMinutes: statsTotals?.avgPrepMinutes ?? average(overallPrepDurations),
        averageEndToEnd: statsTotals?.avgEndToEndMinutes ?? average(overallEndToEnd),
        completionRate: filteredOrders.length ? (deliveredOrders.length / filteredOrders.length) * 100 : 0,
        activeVendors: vendorPerformance.length || vendors.length,
      },
      vendorPerformance,
      popularItems,
      orderStatusData,
      timeseries,
    };
  }, [orders, vendors, timeRange, customDateRange, selectedVendorIds, serverStats, scopedVendorId, STADIUM_TZ]);

  const timeRangeLabel = useMemo(
    () => TIME_RANGE_OPTIONS.find((option) => option.value === timeRange)?.label ?? 'Custom Range',
    [timeRange]
  );

  const orderSpark = analyticsData.timeseries.map((t) => t.orders);
  const revenueSpark = analyticsData.timeseries.map((t) => t.revenue);

  const clearFilters = () => {
    setTimeRange('7d');
    setCustomDateRange({ start: '', end: '' });
    if (!scopedVendorId) setSelectedVendorIds([]);
  };

  const showAllTime = () => {
    setTimeRange('90d');
    setCustomDateRange({ start: '', end: '' });
  };

  const downloadCSV = (data: ReportData, filename: string) => {
    let csvContent = 'Stadium Analytics Report\n\n';
    csvContent += 'Overview\nMetric,Value\n';
    Object.entries(data.overview).forEach(([key, value]) => {
      csvContent += `${key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())},${value}\n`;
    });
    csvContent += '\nVendor Performance\nVendor,Orders,Delivered,Revenue,Avg Basket,Prep (m),End-to-End (m),Completion %\n';
    data.vendorPerformance.forEach((vendor) => {
      csvContent += `${vendor.name},${vendor.orders},${vendor.delivered},${vendor.revenue},${vendor.averageBasket},${vendor.avgPrepMinutes},${vendor.avgEndToEndMinutes},${vendor.completionRate}\n`;
    });
    csvContent += '\nPopular Items\nItem,Orders,Revenue\n';
    data.popularItems.forEach((item) => {
      csvContent += `${item.name},${item.orders},${item.revenue}\n`;
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const downloadJSON = (data: ReportData, filename: string) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExportReport = async () => {
    setIsGeneratingReport(true);
    await new Promise((resolve) => setTimeout(resolve, 400));

    const reportData: ReportData = {
      generatedAt: new Date().toISOString(),
      timeRange,
      customDateRange: timeRange === 'custom' ? customDateRange : null,
      selectedVendors: scopedVendorId ? [scopedVendorId] : selectedVendorIds,
      overview: analyticsData.overview,
      vendorPerformance: analyticsData.vendorPerformance,
      popularItems: analyticsData.popularItems,
      orderStatusData: analyticsData.orderStatusData,
      timeseries: analyticsData.timeseries,
    };

    const filename = `stadium-analytics-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
    if (exportFormat === 'csv') downloadCSV(reportData, filename);
    else downloadJSON(reportData, filename);

    setIsGeneratingReport(false);
    setShowExportModal(false);
  };

  const TrendsCharts: React.FC<{ timeseries: { date: string; orders: number; revenue: number }[] }> = ({ timeseries }) => (
    <div className={styles.trendsCharts}>
      <div className={styles.trendBlock}>
        <div className={styles.trendLegend}>
          <span className={styles.legendDotOrders} />
          Orders
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={timeseries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-primary)" opacity={0.5} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip formatter={(v: number) => [v, 'Orders']} contentStyle={TOOLTIP_STYLE} />
            <Line type="monotone" dataKey="orders" stroke="var(--brand-primary)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className={styles.trendBlock}>
        <div className={styles.trendLegend}>
          <span className={styles.legendDotRevenue} />
          Revenue
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={timeseries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="analyticsRevGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-success)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--color-success)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-primary)" opacity={0.5} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
            <Tooltip formatter={(v: number) => [`$${v.toFixed(2)}`, 'Revenue']} contentStyle={TOOLTIP_STYLE} />
            <Area type="monotone" dataKey="revenue" stroke="var(--color-success)" fill="url(#analyticsRevGrad)" strokeWidth={2} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const PopularItemsChart: React.FC<{ items: { name: string; orders: number; revenue: number }[] }> = ({ items }) => (
    <>
      <div className={styles.popularChartWrap}>
        {items.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={items} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-primary)" opacity={0.5} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--color-text-tertiary)' }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} />
              <Tooltip formatter={(v: number) => [v, 'Orders']} contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="orders" fill="var(--brand-primary)" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className={styles.sectionHint}>No item data for this range.</p>
        )}
      </div>
      <div className={styles.popularListFallback}>
        {items.map((item, index) => (
          <div key={item.name} className={styles.popularListItem}>
            <span className={styles.popularListName}>#{index + 1} {item.name}</span>
            <span className={styles.popularListMeta}>{item.orders} orders</span>
          </div>
        ))}
      </div>
    </>
  );

  const SkeletonBlock = () => (
    <>
      <div className={styles.statRow}>
        {Array.from({ length: 4 }).map((_, i) => (
          <StatTile key={`p-${i}`} icon={<OrdersIcon size={16} />} label="" value="" loading />
        ))}
      </div>
      <div className={`${styles.statRow} ${styles.statRowSecondary}`}>
        {Array.from({ length: 4 }).map((_, i) => (
          <StatTile key={`s-${i}`} icon={<OrdersIcon size={16} />} label="" value="" loading />
        ))}
      </div>
      <div className={`${styles.skeletonBlock} ${styles.skeletonTrends}`} aria-hidden="true" />
      <div className={styles.skeletonCharts}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonChart}`} aria-hidden="true" />
        <div className={`${styles.skeletonBlock} ${styles.skeletonChart}`} aria-hidden="true" />
      </div>
    </>
  );

  const EmptyState = () => (
    <div className={styles.emptyState}>
      <AnalyticsIcon size={48} className={styles.emptyIconSvg} />
      <h3 className={styles.emptyTitle}>No orders for this range</h3>
      <p className={styles.emptyDescription}>
        Try widening the date range or clearing vendor filters to see analytics data.
      </p>
      <div className={styles.emptyActions}>
        <button type="button" className={styles.emptyButton} onClick={clearFilters}>
          Reset Filters
        </button>
        <button type="button" className={styles.emptyButtonSecondary} onClick={showAllTime}>
          Show All Time
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.analytics}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Analytics</h1>
          <p className={styles.subtitle}>Performance insights from live order data</p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.vendorFilter}>
            {scopedVendorId ? (
              <div className={styles.scopedVendorLabel}>
                {vendors.find((v) => String(v.id) === scopedVendorId)?.name || scopedVendorId}
              </div>
            ) : (
              <div className={styles.vendorDropdownWrapper} ref={vendorDropdownRef}>
                <button
                  type="button"
                  className={styles.vendorDropdownTrigger}
                  onClick={() => setVendorDropdownOpen((o) => !o)}
                  aria-expanded={vendorDropdownOpen}
                >
                  {selectedVendorIds.length === 0 ? 'All Vendors' : `${selectedVendorIds.length} selected`}
                </button>
                {vendorDropdownOpen && (
                  <div className={styles.vendorDropdownPanel}>
                    <label className={styles.vendorDropdownItem}>
                      <input
                        type="checkbox"
                        checked={selectedVendorIds.length === 0}
                        onChange={() => setSelectedVendorIds([])}
                        className={styles.vendorCheckbox}
                      />
                      All Vendors
                    </label>
                    {vendors.map((v) => (
                      <label key={String(v.id)} className={styles.vendorDropdownItem}>
                        <input
                          type="checkbox"
                          checked={selectedVendorIds.includes(String(v.id))}
                          onChange={() => {
                            const id = String(v.id);
                            setSelectedVendorIds((prev) =>
                              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                            );
                          }}
                          className={styles.vendorCheckbox}
                        />
                        {v.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className={styles.timeSelect}
            aria-label="Time range"
          >
            {TIME_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button type="button" onClick={() => setShowExportModal(true)} className={styles.exportButton}>
            <DownloadIcon size={16} />
            Export
          </button>
        </div>
      </div>

      <div className={styles.headerMeta}>
        <span>Range: {timeRangeLabel}{timeRange === 'custom' && customDateRange.start && customDateRange.end ? ` (${customDateRange.start} → ${customDateRange.end})` : ''}</span>
      </div>

      {timeRange === 'custom' && (
        <div className={styles.dateRange}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="analytics-start">Start Date</label>
            <input
              id="analytics-start"
              type="date"
              value={customDateRange.start}
              onChange={(e) => setCustomDateRange((prev) => ({ ...prev, start: e.target.value }))}
              className={styles.input}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="analytics-end">End Date</label>
            <input
              id="analytics-end"
              type="date"
              value={customDateRange.end}
              onChange={(e) => setCustomDateRange((prev) => ({ ...prev, end: e.target.value }))}
              className={styles.input}
            />
          </div>
        </div>
      )}

      {loading ? (
        <SkeletonBlock />
      ) : error ? (
        <div className={styles.errorState}>
          <AlertIcon size={24} className={styles.errorIcon} />
          {error}
        </div>
      ) : analyticsData.overview.totalOrders === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className={styles.statRow}>
            <StatTile
              icon={<OrdersIcon size={16} />}
              label="Total Orders"
              value={analyticsData.overview.totalOrders.toLocaleString()}
              spark={orderSpark}
            />
            <StatTile
              icon={<DollarIcon size={16} />}
              label="Revenue"
              value={`$${analyticsData.overview.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              spark={revenueSpark}
              sparkColor="var(--color-success)"
            />
            <StatTile
              icon={<TrendingUpIcon size={16} />}
              label="Avg Basket"
              value={`$${analyticsData.overview.averageOrderValue.toFixed(2)}`}
            />
            <StatTile
              icon={<CheckIcon size={16} />}
              label="Completion Rate"
              value={`${analyticsData.overview.completionRate.toFixed(1)}%`}
              trendTone="success"
            />
          </div>

          <div className={`${styles.statRow} ${styles.statRowSecondary}`}>
            <StatTile
              icon={<CheckIcon size={16} />}
              label="Delivered"
              value={analyticsData.overview.deliveredOrders.toLocaleString()}
            />
            <StatTile
              icon={<OrdersIcon size={16} />}
              label="Cancelled"
              value={analyticsData.overview.cancelledOrders.toLocaleString()}
              trendTone="danger"
            />
            <StatTile
              icon={<ClockIcon size={16} />}
              label="Avg Prep Time"
              value={analyticsData.overview.averagePrepMinutes > 0 ? `${analyticsData.overview.averagePrepMinutes.toFixed(1)}m` : '—'}
              title="Average time from preparing to ready"
            />
            <StatTile
              icon={<ClockIcon size={16} />}
              label="Avg End-to-End"
              value={analyticsData.overview.averageEndToEnd > 0 ? `${analyticsData.overview.averageEndToEnd.toFixed(1)}m` : '—'}
              title="Average time from order created to delivered"
            />
          </div>

          <div className={styles.trendsCard}>
            <h3 className={styles.sectionTitle}>Trends</h3>
            <TrendsCharts timeseries={analyticsData.timeseries} />
          </div>

          <div className={styles.chartsGrid}>
            <div className={styles.chartCard}>
              <h3 className={styles.sectionTitle}>Order Status Distribution</h3>
              <div className={styles.statusList}>
                {analyticsData.orderStatusData.filter((s) => s.count > 0).map((status) => (
                  <div key={status.status} className={styles.statusItem}>
                    <div className={styles.statusLabel}>{status.status.replace(/-/g, ' ')}</div>
                    <div className={styles.statusBar}>
                      <div className={styles.statusFill} style={{ width: `${status.percentage}%` }} />
                    </div>
                    <div className={styles.statusValue}>
                      {status.count} ({status.percentage.toFixed(1)}%)
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.chartCard}>
              <h3 className={styles.sectionTitle}>Top Selling Items</h3>
              <PopularItemsChart items={analyticsData.popularItems} />
            </div>
          </div>

          <div className={styles.tableContainer}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Vendor Performance</h3>
              <span className={styles.vendorChip}>
                {analyticsData.overview.activeVendors} active vendor{analyticsData.overview.activeVendors !== 1 ? 's' : ''}
              </span>
            </div>
            <p className={styles.sectionHint}>Sorted by revenue within the selected window</p>
            <div className={styles.table}>
              <div className={styles.tableHeader}>
                <div className={styles.tableCell}>Vendor</div>
                <div className={styles.tableCell}>Orders</div>
                <div className={styles.tableCell}>Delivered</div>
                <div className={styles.tableCell}>Revenue</div>
                <div className={styles.tableCell}>Avg Basket</div>
                <div className={styles.tableCell}>Prep</div>
                <div className={styles.tableCell}>End-to-End</div>
                <div className={styles.tableCell}>Completion</div>
              </div>
              {analyticsData.vendorPerformance.map((vendor, index) => (
                <div key={vendor.vendorId || index} className={styles.tableRow}>
                  <div className={styles.tableCell}><strong>{vendor.name}</strong></div>
                  <div className={styles.tableCell}>{vendor.orders}</div>
                  <div className={styles.tableCell}>{vendor.delivered}</div>
                  <div className={styles.tableCell}>${vendor.revenue.toLocaleString()}</div>
                  <div className={styles.tableCell}>${vendor.averageBasket.toFixed(2)}</div>
                  <div className={styles.tableCell}>{vendor.avgPrepMinutes.toFixed(1)}m</div>
                  <div className={styles.tableCell}>{vendor.avgEndToEndMinutes.toFixed(1)}m</div>
                  <div className={styles.tableCell}>{vendor.completionRate.toFixed(1)}%</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Modal
        isOpen={showExportModal}
        onClose={() => !isGeneratingReport && setShowExportModal(false)}
        title="Export Analytics Report"
        size="md"
        footer={
          <div className={styles.modalActions}>
            <button type="button" className="btn btn--secondary" onClick={() => setShowExportModal(false)} disabled={isGeneratingReport}>
              Cancel
            </button>
            <button type="button" className="btn btn--primary" onClick={handleExportReport} disabled={isGeneratingReport}>
              {isGeneratingReport ? 'Exporting…' : 'Export Report'}
            </button>
          </div>
        }
      >
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="export-format">Export Format</label>
          <select
            id="export-format"
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className={styles.select}
          >
            <option value="csv">CSV File</option>
            <option value="json">JSON Data</option>
          </select>
        </div>
        <p className={styles.sectionHint}>
          Exports overview metrics, vendor performance, popular items, and timeseries for the current filters.
        </p>
      </Modal>
    </div>
  );
};

export default Analytics;
